import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { parseAccessTokenClaims } from './session.js';
import { useAuth } from './AuthProvider.js';

export type AccountMenuExtraItem = {
  id: string;
  label: string;
  href?: string;
  onSelect?: () => void;
};

export interface AccountMenuProps {
  /** Display name / email on the trigger and menu header. */
  label?: string;
  subtitle?: string;
  /** Optional links/actions above Sign out (e.g. customer Account). */
  extraItems?: AccountMenuExtraItem[];
  /** Runs after successful logout, before navigate (clear surface storage). */
  onAfterLogout?: () => void;
  /** Override post-logout navigation (defaults to `useAuth().signInPath`). */
  signInPath?: string;
  className?: string;
  triggerClassName?: string;
  avatarClassName?: string;
  labelClassName?: string;
  menuClassName?: string;
  /** Custom trigger contents; receives open state + resolved label. */
  renderTrigger?: (ctx: {
    open: boolean;
    label: string;
    initials: string;
  }) => ReactNode;
  signOutLabel?: string;
  /** Hide text next to avatar on the trigger. */
  avatarOnly?: boolean;
}

function initialsFromLabel(label: string): string {
  return label
    .split(/[\s@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Best-effort display label from JWT claims when the app does not pass one. */
export function accountLabelFromToken(
  accessToken: string | null,
  fallback = 'Account',
): string {
  const claims = parseAccessTokenClaims(accessToken);
  if (claims.sub) {
    return claims.sub.length > 18 ? `${claims.sub.slice(0, 10)}…` : claims.sub;
  }
  return fallback;
}

/**
 * Header avatar menu with Sign out — shared across merchant / customer / admin / business.
 * Uses surface logout from `useAuth()` (POST /{surface}/v1/auth/logout).
 */
export function AccountMenu({
  label: labelProp,
  subtitle,
  extraItems,
  onAfterLogout,
  signInPath: signInPathProp,
  className,
  triggerClassName,
  avatarClassName,
  labelClassName,
  menuClassName,
  renderTrigger,
  signOutLabel = 'Sign out',
  avatarOnly = false,
}: AccountMenuProps) {
  const { logout, accessToken, authenticated, signInPath: authSignInPath } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const label = labelProp?.trim() || accountLabelFromToken(accessToken);
  const initials = initialsFromLabel(label) || 'A';
  const signInPath = signInPathProp ?? authSignInPath;

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!authenticated) return null;

  async function signOut() {
    setBusy(true);
    try {
      await logout();
      onAfterLogout?.();
      navigate(signInPath);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  return (
    <div ref={rootRef} className={className ? `relative ${className}` : 'relative'} data-testid="account-menu">
      <button
        type="button"
        className={
          triggerClassName ??
          'flex min-h-11 items-center gap-inline rounded-control border border-line bg-raised px-field py-xs text-body no-underline hov:bg-sunken'
        }
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        {renderTrigger ? (
          renderTrigger({ open, label, initials })
        ) : (
          <>
            <span
              className={
                avatarClassName ??
                'inline-flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-pill bg-sunken text-body-sm font-medium text-muted'
              }
              aria-hidden
            >
              {initials}
            </span>
            {avatarOnly ? null : (
              <span
                className={
                  labelClassName ?? 'max-w-[10rem] truncate text-body-sm font-medium text-default'
                }
              >
                {label}
              </span>
            )}
          </>
        )}
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className={
            menuClassName ??
            'absolute right-0 z-[var(--z-overlay,40)] mt-xs min-w-[14rem] rounded-surface border border-line bg-raised p-field shadow-sticky'
          }
        >
          <p className="m-0 truncate text-body font-medium text-default">{label}</p>
          {subtitle ? <p className="m-0 mt-xs text-body-sm text-muted">{subtitle}</p> : null}
          {extraItems?.length ? (
            <div className="mt-field flex flex-col gap-xs border-t border-line-subtle pt-field">
              {extraItems.map((item) =>
                item.href ? (
                  <Link
                    key={item.id}
                    to={item.href}
                    role="menuitem"
                    className="flex min-h-11 items-center rounded-control px-field text-body no-underline text-default hov:bg-sunken"
                    onClick={() => {
                      setOpen(false);
                      item.onSelect?.();
                    }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    className="flex min-h-11 w-full items-center rounded-control border-0 bg-transparent px-field text-left text-body text-default hov:bg-sunken"
                    onClick={() => {
                      setOpen(false);
                      item.onSelect?.();
                    }}
                  >
                    {item.label}
                  </button>
                ),
              )}
            </div>
          ) : null}
          <div className="mt-field border-t border-line-subtle pt-field">
            <button
              type="button"
              role="menuitem"
              disabled={busy}
              data-testid="account-menu-sign-out"
              className="flex min-h-11 w-full items-center justify-center rounded-control border border-line bg-secondary px-field text-body text-default hov:bg-secondary-hover disabled:opacity-60"
              onClick={() => void signOut()}
            >
              {busy ? 'Signing out…' : signOutLabel}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
